/**
 * 数表記反復演算子 `'` の展開（POUCHES 式評価の前処理）
 */

export type NrepExpandResult =
  | { ok: true; expanded: string }
  | { ok: false };

class NrepError extends Error {}

/** 整数反復 `'` の最大回数（これを超えると展開せずエラー） */
const MAX_NREP_REPEAT_COUNT = 1000;
/** 反復展開後の最大文字数 */
const MAX_NREP_OUTPUT_LENGTH = 10_000;

function isDigit(ch: string): boolean {
  return ch >= '0' && ch <= '9';
}

function formatExpandedNumber(value: number): string {
  if (!Number.isFinite(value)) {
    throw new NrepError('non-finite number');
  }
  if (Number.isInteger(value)) {
    return String(value);
  }
  const s = String(value);
  if (s.includes('e') || s.includes('E')) {
    return value.toFixed(12).replace(/\.?0+$/, '');
  }
  return s;
}

function repeatPattern(pattern: string, count: number): string {
  if (count < 0) {
    throw new NrepError('negative repetition count');
  }
  if (count === 0) {
    return '0';
  }
  if (count > MAX_NREP_REPEAT_COUNT) {
    throw new NrepError('repetition count too large');
  }
  const totalLen = pattern.length * count;
  if (totalLen > MAX_NREP_OUTPUT_LENGTH) {
    throw new NrepError('repetition output too large');
  }
  return pattern.repeat(count);
}

function valBase(text: string): number {
  return text === '' ? 0 : parseInt(text, 10);
}

function buildDecimalFinite(
  intPart: string,
  prefix: string,
  pattern: string,
  count: number,
): number {
  if (count > MAX_NREP_REPEAT_COUNT) {
    throw new NrepError('repetition count too large');
  }
  const intValue = parseInt(intPart, 10);
  const prefixValue = prefix ? valBase(prefix) / 10 ** prefix.length : 0;
  const k = pattern.length;
  const kn = k * count;
  const patternValue = valBase(pattern);
  const numerator = patternValue * (10 ** kn - 1);
  const denominator = (10 ** k - 1) * 10 ** kn;
  return intValue + prefixValue + numerator / denominator;
}

function buildDecimalInfinite(
  intPart: string,
  prefix: string,
  pattern: string,
): number {
  const intValue = parseInt(intPart, 10);
  const prefixValue = prefix ? valBase(prefix) / 10 ** prefix.length : 0;
  const k = pattern.length;
  const patternValue = valBase(pattern);
  let repeatValue = patternValue / (10 ** k - 1);
  if (prefix) {
    repeatValue /= 10 ** prefix.length;
  }
  return intValue + prefixValue + repeatValue;
}

function readDigits(source: string, pos: number): { text: string; end: number } {
  let i = pos;
  while (i < source.length && isDigit(source[i])) {
    i++;
  }
  if (i === pos) {
    throw new NrepError('expected digits');
  }
  return { text: source.slice(pos, i), end: i };
}

function tryExpandDecimalAt(source: string, pos: number): { value: number; end: number } | null {
  if (!isDigit(source[pos])) {
    return null;
  }
  const intPart = readDigits(source, pos);
  if (source[intPart.end] !== '.') {
    return null;
  }
  let i = intPart.end + 1;

  if (i < source.length && source[i] === '(') {
    i++;
    const pattern = readDigits(source, i);
    i = pattern.end;
    if (source[i] !== "'") {
      return null;
    }
    i++;
    if (i >= source.length || source[i] === ')') {
      const value = buildDecimalInfinite(intPart.text, '', pattern.text);
      if (source[i] === ')') {
        i++;
      }
      return { value, end: i };
    }
    if (!isDigit(source[i])) {
      return null;
    }
    const count = readDigits(source, i);
    i = count.end;
    if (source[i] !== ')') {
      return null;
    }
    return {
      value: buildDecimalFinite(intPart.text, '', pattern.text, parseInt(count.text, 10)),
      end: i + 1,
    };
  }

  if (i >= source.length || !isDigit(source[i])) {
    return null;
  }
  const prefix = readDigits(source, i);
  i = prefix.end;

  if (i < source.length && source[i] === '(') {
    i++;
    const pattern = readDigits(source, i);
    i = pattern.end;
    if (source[i] !== "'") {
      return null;
    }
    i++;
    if (i >= source.length || source[i] !== ')') {
      return null;
    }
    return {
      value: buildDecimalInfinite(intPart.text, prefix.text, pattern.text),
      end: i + 1,
    };
  }

  if (i < source.length && source[i] === "'") {
    i++;
    if (i >= source.length || !isDigit(source[i])) {
      if (prefix.text.length === 1) {
        return {
          value: buildDecimalInfinite(intPart.text, prefix.text, prefix.text),
          end: i,
        };
      }
      return null;
    }
    const count = readDigits(source, i);
    if (prefix.text.length === 1) {
      return {
        value: buildDecimalFinite(intPart.text, '', prefix.text, parseInt(count.text, 10)),
        end: count.end,
      };
    }
    return null;
  }

  return null;
}

function expandDecimalRepetitions(source: string): string {
  let out = '';
  let i = 0;
  while (i < source.length) {
    const dec = tryExpandDecimalAt(source, i);
    if (dec) {
      out += formatExpandedNumber(dec.value);
      i = dec.end;
      continue;
    }
    out += source[i];
    i++;
  }
  return out;
}

function findMatchingParen(source: string, openPos: number): number {
  let depth = 0;
  for (let j = openPos; j < source.length; j++) {
    if (source[j] === '(') {
      depth++;
    } else if (source[j] === ')') {
      depth--;
      if (depth === 0) {
        return j;
      }
    }
  }
  return -1;
}

function evaluateAddSubExpression(expr: string): number {
  const expanded = expandIntegerExpression(expr);
  const tokens: string[] = [];
  let current = '';
  for (let j = 0; j < expanded.length; j++) {
    const ch = expanded[j];
    if ((ch === '+' || ch === '-') && current !== '') {
      tokens.push(current);
      tokens.push(ch);
      current = '';
    } else if (ch === '+' || ch === '-') {
      current = ch;
    } else {
      current += ch;
    }
  }
  if (current) {
    tokens.push(current);
  }
  if (tokens.length === 0) {
    throw new NrepError('empty expression');
  }
  let result = parseFloat(tokens[0]);
  for (let j = 1; j < tokens.length; j += 2) {
    const op = tokens[j];
    const rhs = parseFloat(tokens[j + 1]);
    result = op === '+' ? result + rhs : result - rhs;
  }
  return result;
}

function expandGroupedIntegerRepetitions(source: string): string {
  let out = '';
  let i = 0;
  while (i < source.length) {
    if (source[i] === '(') {
      const close = findMatchingParen(source, i);
      if (close < 0) {
        throw new NrepError('unmatched paren');
      }
      const inner = source.slice(i + 1, close);
      const after = source.slice(close + 1);
      const repMatch = after.match(/^('{1,2})(\d+)/);
      if (repMatch && !inner.includes('.')) {
        const count = parseInt(repMatch[2], 10);
        const innerValue = evaluateAddSubExpression(inner);
        if (innerValue < 0) {
          throw new NrepError('negative grouped value');
        }
        const pattern = String(Math.trunc(innerValue));
        out += repeatPattern(pattern, count);
        i = close + 1 + repMatch[0].length;
        continue;
      }
    }
    out += source[i];
    i++;
  }
  return out;
}

function foldPowRepChain(segment: string): string {
  if (!segment) {
    throw new NrepError('empty segment');
  }

  let i = 0;
  let sign = '';
  if (segment[i] === '-') {
    sign = '-';
    i++;
  }

  const readDigitsLocal = (): string => {
    const start = i;
    while (i < segment.length && isDigit(segment[i])) {
      i++;
    }
    if (i === start) {
      throw new NrepError('expected digits');
    }
    return segment.slice(start, i);
  };

  let acc = readDigitsLocal();

  while (i < segment.length) {
    if (segment[i] === '^') {
      i++;
      let expSign = '';
      if (segment[i] === '-') {
        expSign = '-';
        i++;
      }
      const expDigits = readDigitsLocal();
      const base = parseFloat(sign + acc);
      const exp = parseFloat(expSign + expDigits);
      acc = formatExpandedNumber(Math.pow(base, exp));
      sign = '';
      continue;
    }
    if (segment[i] === "'") {
      const fixed = segment[i + 1] === "'";
      i += fixed ? 2 : 1;
      if (i >= segment.length || !isDigit(segment[i])) {
        throw new NrepError('infinite integer repetition');
      }
      const start = i;
      while (i < segment.length && isDigit(segment[i])) {
        i++;
      }
      const count = parseInt(segment.slice(start, i), 10);
      acc = repeatPattern(sign + acc, count);
      sign = '';
      continue;
    }
    break;
  }

  return sign + acc;
}

function expandColonInSegment(segment: string): string {
  const colonIndex = segment.indexOf(':');
  if (colonIndex < 0) {
    return foldPowRepChain(segment);
  }
  const left = segment.slice(0, colonIndex);
  const right = segment.slice(colonIndex + 1);
  return `${foldPowRepChain(left)}:${foldPowRepChain(right)}`;
}

function expandIntegerTerm(term: string): string {
  if (!term) {
    return term;
  }

  let out = '';
  let i = 0;
  let pendingSign = '';

  if (term[i] === '+' || term[i] === '-') {
    pendingSign = term[i];
    i++;
  }

  while (i < term.length) {
    let segmentEnd = i;
    while (segmentEnd < term.length && !['*', '/'].includes(term[segmentEnd])) {
      segmentEnd++;
    }
    const segment = term.slice(i, segmentEnd);
    const expanded = segment.includes(':') ? expandColonInSegment(segment) : foldPowRepChain(segment);
    out += pendingSign + expanded;
    pendingSign = '';

    if (segmentEnd < term.length) {
      out += term[segmentEnd];
      i = segmentEnd + 1;
      if (i < term.length && (term[i] === '+' || term[i] === '-')) {
        pendingSign = term[i];
        i++;
      }
    } else {
      break;
    }
  }

  return out;
}

function splitAddSubTop(source: string): { parts: string[]; ops: string[] } {
  const parts: string[] = [];
  const ops: string[] = [];
  let current = '';
  let i = 0;
  if (source[i] === '+' || source[i] === '-') {
    current = source[i];
    i++;
  }
  while (i < source.length) {
    const ch = source[i];
    if (ch === '+' || ch === '-') {
      parts.push(current);
      ops.push(ch);
      current = '';
      i++;
    } else {
      current += ch;
      i++;
    }
  }
  parts.push(current);
  return { parts, ops };
}

function expandIntegerExpression(source: string): string {
  if (!source.includes("'") && !source.includes('^')) {
    return source;
  }
  const { parts, ops } = splitAddSubTop(source);
  const expandedParts = parts.map((p, idx) => {
    if (!p || (idx === 0 && (p === '+' || p === '-'))) {
      return p;
    }
    return expandIntegerTerm(p);
  });
  let out = expandedParts[0] ?? '';
  for (let j = 0; j < ops.length; j++) {
    out += ops[j] + expandedParts[j + 1];
  }
  return out;
}

export function expandNumeralRepetition(expression: string): NrepExpandResult {
  try {
    let s = expression.replace(/\s/g, '');
    if (!s.includes("'")) {
      return { ok: true, expanded: s };
    }
    s = expandDecimalRepetitions(s);
    s = expandGroupedIntegerRepetitions(s);
    s = expandIntegerExpression(s);
    return { ok: true, expanded: s };
  } catch {
    return { ok: false };
  }
}
